import { ChangeMemberData, ChangeMemberType } from 'components/pages/Client/WorkspacesProfile/_main/types'
import { MemberTableRow, getRowSortName } from './MembersTable.component'
import { SeatType, SeatTypeItem, getMemberSeatType } from '../SeatTypeDropdown'
import { SubscriptionPlanType, WorkspaceMemberProfile, WorkspaceRole, WorkspaceSeatType } from 'models/user/userWorkspaceDTOs'
import { useAllowedSeatTypes, useWorkspacePlan } from 'utils/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetWorkspaceMembers, useRevokeWorkspaceInvitation } from 'dataQueries'

import { Nullable } from 'models/helpers/helperTypes'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspacesProfileContext } from 'components/pages/Client/WorkspacesProfile/_main/WorkspacesProfile.context'

/**
 * Whether promoting this member to admin must be blocked (ISSUE-2613). Per the 2026-07-28 permission
 * review, an admin of a PAID workspace always holds a Full seat — restricted admins exist only in free
 * workspaces — so a member on a Restricted or Sponsored seat cannot be promoted in a paid one. Only the
 * grant direction is gated: revoking admin stays available whatever the admin's own seat type, and a
 * restricted admin keeps every other admin action (the meeting explicitly rejected differentiating by
 * the target member's seat type). Exported for unit testing.
 */
export const isGrantAdminBlocked = (isPaidPlan: boolean, memberRole: WorkspaceRole | undefined, memberSeatType: WorkspaceSeatType | undefined): boolean =>
  isPaidPlan && memberRole !== WorkspaceRole.ADMIN && memberSeatType !== WorkspaceSeatType.FULL

export const [MembersContentContextProvider, useMembersContentContext] = constate(() => {
  const { t } = useTranslation(['workspace_profile'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { workspaceId, workspaceData, workspaceDataOfUser, isSingleAdminInWorkspace, pendingInvitations, allUserWorkspacesResponse } = useWorkspacesProfileContext()
  const { currentUserWorkspace } = useUserData()
  const { isWorkspacePersonal } = useWorkspacePlan()

  // The acting user's own seat, falling back to the currently selected workspace's seat (ISSUE-2488).
  // `workspaceDataOfUser.seatType` derives from `workspaceData.members` — the array this ticket's
  // backend counterpart hides from restricted seats — so it can vanish for a restricted admin and
  // silently lift the Restricted cap. `currentUserWorkspace.seatType` comes from `/user/me` and is
  // always present; under /workspace-settings the provider is scoped to currentUserWorkspace.id, so it
  // is the same (selected) workspace. Keeps the cap closed even when the members list is unavailable.
  const actingSeatType = workspaceDataOfUser?.seatType ?? currentUserWorkspace?.seatType

  // Seat types this workspace's plan permits assigning, expanded once the restricted member feature
  // is enabled (ISSUE-2078). Drives the seat-type dropdown and its editability. Capped to Restricted
  // when the acting user's own seat is Restricted — they cannot grant paid seats (ISSUE-2488).
  const { allowedSeatTypes, planType } = useAllowedSeatTypes(workspaceData?.plan, isWorkspacePersonal, actingSeatType)

  // Paid plans (Pro / Enterprise) must never hold a restricted admin (ISSUE-2613). An unresolved plan
  // counts as paid so the gate stays closed while the workspace query is still loading — the cost is a
  // briefly disabled action, whereas failing open would let a promotion through in a paid workspace.
  const isPaidPlan = planType !== SubscriptionPlanType.PERSONAL

  // Gate on the VIEWED workspace (route-driven) rather than the user's active/stored workspace.
  // Under multiworkspace the workspace at /workspaces/:id can differ from currentUserWorkspace,
  // so deriving these from useWorkspaceRoles() (active workspace) would gate UI on the wrong workspace.
  const isViewedWorkspaceAdmin = workspaceDataOfUser?.role === WorkspaceRole.ADMIN
  const isViewedWorkspaceMember = workspaceDataOfUser?.role === WorkspaceRole.MEMBER
  const isViewedWorkspaceSponsored = workspaceDataOfUser?.seatType === WorkspaceSeatType.SPONSORED

  const { data: members, isPending: isMembersPending } = useGetWorkspaceMembers(workspaceId)
  const isLoading = isMembersPending || allUserWorkspacesResponse.isPending
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false)
  const [changeMemberRole, setChangeMemberRole] = useState<Nullable<ChangeMemberData>>(null)
  const [removeMember, setRemoveMember] = useState<WorkspaceMemberProfile | undefined>()
  const revokeInvitation = useRevokeWorkspaceInvitation()
  const [revokedInvitationIds, setRevokedInvitationIds] = useState<Set<string>>(new Set())

  // Clear optimistic filter once server data has refreshed
  useEffect(() => {
    if (revokedInvitationIds.size > 0) setRevokedInvitationIds(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInvitations])

  const showCanOrderColumn = isViewedWorkspaceAdmin && !isWorkspacePersonal

  // Gate on the VIEWED workspace (route-driven) rather than the user's active workspace.
  // Only an admin can manage seat types, the plan must offer more than one seat type to choose
  // from, and the sole admin cannot restrict their OWN access — that would leave the workspace
  // without a full admin, mirroring how isActionCellDisabled blocks the sole admin from removing
  // their admin role.
  const isAccessCellDisabled = useCallback((member: WorkspaceMemberProfile) => workspaceDataOfUser?.role !== WorkspaceRole.ADMIN || allowedSeatTypes.length <= 1 || (member.role === WorkspaceRole.ADMIN && isSingleAdminInWorkspace), [workspaceDataOfUser, allowedSeatTypes, isSingleAdminInWorkspace])

  const isActionCellDisabled = useCallback((member: WorkspaceMemberProfile) => (member.role === WorkspaceRole.ADMIN && isSingleAdminInWorkspace) || isViewedWorkspaceMember || isViewedWorkspaceSponsored, [isSingleAdminInWorkspace, isViewedWorkspaceMember, isViewedWorkspaceSponsored])

  // Gates only the grant-admin item inside the actions menu (ISSUE-2613) — remove & transfer stays
  // available, so this cannot be folded into isActionCellDisabled.
  const isGrantAdminDisabled = useCallback((member: WorkspaceMemberProfile) => isGrantAdminBlocked(isPaidPlan, member.role, member.seatType), [isPaidPlan])

  const sortedRows = useMemo<MemberTableRow[]>(() => {
    const memberRows: MemberTableRow[] = (members ?? []).map((m) => ({ type: 'member', data: m }))
    const pending: MemberTableRow[] = (pendingInvitations ?? [])
      .filter((p) => !revokedInvitationIds.has(p.id))
      .map((p) => ({ type: 'pending', data: p }))
    return [...memberRows, ...pending].sort((a, b) => {
      const nameA = getRowSortName(a)
      const nameB = getRowSortName(b)
      if (nameA > nameB) return 1
      if (nameA < nameB) return -1
      return 0
    })
  }, [members, pendingInvitations, revokedInvitationIds])

  const onGrantOrRevokeAdmin = useCallback((member: WorkspaceMemberProfile) => {
    const newRole = member.role === WorkspaceRole.ADMIN ? WorkspaceRole.MEMBER : WorkspaceRole.ADMIN

    logger.info('Workspace member role change requested', undefined, { sentry: true, context: { component: 'MembersContent', action: 'grantOrRevokeAdmin', metadata: { workspaceId, memberId: member.userId, fromRole: member.role, toRole: newRole } } })

    setChangeMemberRole({
      type: ChangeMemberType.ROLE,
      memberId: member.userId,
      name: member.name,
      email: member.email,
      newRole,
      // Role and seat type are orthogonal — preserve the member's seat type instead of forcing FULL.
      seatType: member.seatType,
    })
  }, [workspaceId])

  const onChangeSeatType = useCallback((member: WorkspaceMemberProfile, item: SeatTypeItem) => {
    const currentSeatType = getMemberSeatType(member)
    if (currentSeatType === item.type) return

    const seatType = item.type === SeatType.SPONSORED_ACCESS ? WorkspaceSeatType.SPONSORED : item.type === SeatType.RESTRICTED_ACCESS ? WorkspaceSeatType.RESTRICTED : WorkspaceSeatType.FULL

    logger.info('Workspace member seat type change requested', undefined, { sentry: true, context: { component: 'MembersContent', action: 'changeSeatType', metadata: { workspaceId, memberId: member.userId, fromSeatType: currentSeatType, toSeatType: item.type } } })

    setChangeMemberRole({
      type: ChangeMemberType.ACCESS,
      memberId: member.userId,
      name: member.name,
      email: member.email,
      newRole: member.role,
      seatType,
    })
  }, [workspaceId])

  const onChangeMemberCanOrder = useCallback((member: WorkspaceMemberProfile, canOrder: boolean) => {
    if (member.canOrder === canOrder) return

    logger.info('Workspace member can-order change requested', undefined, { sentry: true, context: { component: 'MembersContent', action: 'changeMemberCanOrder', metadata: { workspaceId, memberId: member.userId, fromCanOrder: member.canOrder, toCanOrder: canOrder } } })

    setChangeMemberRole({
      type: ChangeMemberType.CAN_ORDER,
      memberId: member.userId,
      name: member.name,
      email: member.email,
      newRole: member.role,
      // Toggling can-order must not change the seat type — preserve the member's actual seat type.
      seatType: member.seatType,
      canOrder,
    })
  }, [workspaceId])

  const onRevokeInvitation = useCallback((invitationId: string) => {
    revokeInvitation.mutate({ workspaceId, invitationId }, {
      onSuccess: () => {
        // eliminating the visual gap while the data refetches
        setRevokedInvitationIds((prev) => new Set(prev).add(invitationId))
        spawnSuccessToast(t('workspace_profile:invitation_revoked'))
      },
      onError: (error) => {
        logger.error('Failed to revoke workspace invitation', error, { context: { component: 'MembersContent', action: 'revokeInvitation' } })
        spawnErrorToast(t('workspace_profile:invitation_revoke_error'))
      },
    })
  }, [revokeInvitation, workspaceId, spawnSuccessToast, spawnErrorToast, t])

  const isRevokeInvitationPending = useCallback((invitationId: string) => revokeInvitation.isPending && revokeInvitation.variables?.invitationId === invitationId, [revokeInvitation.isPending, revokeInvitation.variables?.invitationId])

  return {
    workspaceId,
    isLoading,
    sortedRows,
    isWorkspacePersonal,
    isViewedWorkspaceAdmin,
    showCanOrderColumn,
    allowedSeatTypes,
    isPaidPlan,
    isAccessCellDisabled,
    isActionCellDisabled,
    isGrantAdminDisabled,
    onGrantOrRevokeAdmin,
    onChangeSeatType,
    onChangeMemberCanOrder,
    onRevokeInvitation,
    isRevokeInvitationPending,
    isAddMembersOpen,
    setIsAddMembersOpen,
    changeMemberRole,
    setChangeMemberRole,
    removeMember,
    setRemoveMember,
  }
})
